import Sugar from 'sugar';

function isNumber(value: unknown) {
  return typeof value === 'number' && isFinite(value);
}

function toNumber(value: unknown): number {
  const num = Number(value);
  if (!isNumber(num)) throw new Error('Value is not a number');
  return num;
}

const uniqueCacheMap = new Map<string, Set<string>>();

const validators: {
  [key: string]: (...args: unknown[]) => string | undefined;
} = {
  required: (val: unknown) => {
    return val === undefined || val === null || val === '' ? 'Field is required but has no value' : undefined;
  },
  minLength: (val: unknown, ruleValue: unknown) => {
    const value = val as { length: number };
    const length = toNumber(ruleValue);
    if (value.length < length) return `Value must have the minimum of ${length} characters`;
  },
  min: (val: unknown, ruleValue: unknown) => {
    const length = toNumber(val as number);
    const min = toNumber(ruleValue as number);
    if (length && length < min) return `Value must be greater than or equal to ${min}`;
  },
  max: (val: unknown, ruleValue: unknown) => {
    const length = toNumber(val as number);
    const max = toNumber(ruleValue as number);
    if (length && length > max) return `Value must be less than or equal to ${max}`;
  },
  greaterThanZero: (val: unknown) => {
    const length = toNumber(val as number);
    if (length <= 0) return 'Value must be greater than 0';
  },
  email: (val: unknown) => {
    const value = String(val as string).toLowerCase();
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      value,
    )
      ? undefined
      : 'Invalid email address';
  },
  date: (val: unknown) => {
    const value = val as Date;
    if (!value || !Sugar.Date.isValid(value)) return 'Invalid date';
  },
  futureDate: (val: unknown) => {
    const value = val as Date;
    const today = Sugar.Date.create('beginning of today');
    if (!value || (!Sugar.Date.is(today, value) && !Sugar.Date.isBefore(today, value)))
      return 'Please provide a date in the future';
  },
};

function validateRule(rule: string, value: any, path: string) {
  const parts = rule.split(':');
  const left = parts.shift();

  if (!left) throw new Error(`Invalid or no rule provided for ${path}`);

  if (left === 'unique') {
    // Handle checking that item is unique
    const set = uniqueCacheMap.get(path);
    if (set === undefined) {
      uniqueCacheMap.set(path, new Set([value]));
    } else {
      if (set.has(value)) return 'Value must be unique';

      set.add(value);
    }

    return false;
  } else if (!validators[left]) throw new Error(`Unknown validator ${left} for ${path}`);

  return validators[left](value, ...parts) || false;
}

export function validate(paths: { [key: string]: string[] }, input: { [key: string]: unknown }) {
  const errors: { [key: string]: { [key: string]: string } } = {};

  Object.keys(paths).forEach((path) => {
    const camelPath = _pathToCamel(path);
    let rules = paths[path];
    if (!rules) return;
    const value = _get(path, input);

    // Validation for optional rules
    if (rules.includes('optional') && (value === undefined || value === null || value === '')) return;
    rules = rules.filter((r) => r !== 'optional');

    const results = rules.reduce((acc: { [key: string]: string }, rule) => {
      const res = validateRule(rule, value, path);
      if (typeof res === 'string') acc[rule] = res;
      return acc;
    }, {});

    if (Object.keys(results).length > 0) {
      errors[camelPath] = results;
    }
  });

  uniqueCacheMap.clear();

  return Object.keys(errors).length < 1 ? true : errors;
}

function _pathToCamel(str: string) {
  return str.replace(/^.|\../g, function (letter: string, index: number) {
    return index === 0 ? letter.toLowerCase() : letter.substring(1).toUpperCase();
  });
}
function _get(path: string, root?: any): any {
  const parts = path.toString().split('.');
  let prop: any = root;

  for (let i = 0; i < parts.length; i += 1) {
    if (!prop) return undefined;
    const part = parts[i];
    prop = prop instanceof Map ? prop.get(part) : prop[part];
  }

  return prop;
}
function _set(path: string, value: any, root: any): string | undefined {
  const parts = path.toString().split('.');
  let prop: any = root;

  const last = parts[parts.length - 1];
  if (parts.length > 1) {
    for (let i = 0; i < parts.length - 1; i += 1) {
      const part = parts[i];
      prop = prop instanceof Map ? prop.get(part) : prop[part];
      if (!prop) return undefined;
    }
    // Set value to object at end of path
    if (prop instanceof Map) {
      prop.set(last, value);
    } else {
      prop[last] = value;
    }
  } else if (prop instanceof Map) {
    prop.set(path, value);
  } else {
    prop[path] = value;
  }

  return parts.join('.');
}

function _applyRule(rule: unknown, value: string | number | boolean | Date) {
  if (rule === 'string') {
    if (value === undefined || value === null) value = '';
    if (typeof value !== 'string' && value.toString) value = value.toString();
    if (typeof value !== 'string') value = '';
    value = value.trim();
  } else if (rule === 'number') {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return value;

    return Number(value.toString().replace(/[^0-9\.-]+/g, ''));
  } else if (rule === 'boolean') {
    if (typeof value === 'boolean') return value ? true : false;
    if (typeof value === 'string') {
      value = value.toUpperCase();
      if (value === 'FALSE') return false;
      if (value === 'TRUE') return true;
    }
  } else if (rule === 'date') {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'boolean') return null;
    return Sugar.Date.create(value);
  }

  return value;
}

export function sanitise(paths: { [key: string]: string[] }, data: { [key: string]: unknown }) {
  Object.keys(paths).forEach((path) => {
    const rules = paths[path];
    if (!rules) return;

    rules.forEach((rule) => {
      _set(path, _applyRule(rule, _get(path, data)), data);
    });
  });

  return data;
}
